//Library Imports
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

//API
import api from "../../../services/api";

//Core Components
import Table from "../../../core/common/dataTable";
import Loader from "../../../core/common/loader";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import DeleteModal from "../../../core/modals/deleteModal";
import CommonSelect from "../../../core/common/commonSelect";
import PredefinedDateRanges from "../../../core/common/datePicker";

//Helpers
import { truncateCompanyText } from "../../../helper/company_helper";
import { exportToExcel, exportToPDF } from "../../../utils/exportData";
import { all_routes } from "../../router/all_routes";

//Components
import AddLocation from "./components/AddLocation";
import EditLocation from "./components/EditLocation";

//Services
import { notify } from "../../../services/toast.config";

const EMPTY_COMPANY = {
  company_name: "Select Company",
  company_id: "",
  company_type: "Type",
};

const AssetsLocation = () => {
  // 🔹 Local Storage
  const signature = localStorage.getItem("signature");

  // 🔹 State: UI
  const [isLoading, setIsLoading] = useState(false);

  // 🔹 State: Data
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [formDataCompanyChoose, setFormDataCompanyChoose] =
    useState(EMPTY_COMPANY);

  // 🔹 State: Filters
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [selectedStartDate, setSelectedStartDate] =
    useState<moment.Moment | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<moment.Moment | null>(
    null
  );

  // 🔹 State: Modal Actions
  const [selectedLocationEdit, setSelectedLocationEdit] = useState<any | null>(
    null
  );
  const [deleteRecord, setDeleteRecord] = useState<{
    id: number;
    Location: string;
  } | null>(null);

  // Derived Data
  const companyOptions = companies.map((company) => ({
    value: company.id.toString(),
    label: `${company.name} - ${company.type}`,
    companyName: company.name,
  }));

  const activeFiltersCount = [
    statusFilter,
    selectedStartDate,
    selectedEndDate,
  ].filter(Boolean).length;

  // Handlers: Form Inputs
  const handleCompanySelection = (selectedOption: any) => {
    if (!selectedOption) return;

    const newFormData = {
      company_id: selectedOption.value,
      company_name:
        selectedOption.label.split("-")[0]?.trim() || "All Companies",
      company_type: selectedOption.label.split("-")[1]?.trim() || "Type",
    };

    localStorage.setItem("selectedAssetCompany", JSON.stringify(newFormData));

    setFormDataCompanyChoose((prev: any) => ({
      ...prev,
      ...newFormData,
    }));
  };

  const handleDateChange = (start: moment.Moment, end: moment.Moment) => {
    setSelectedStartDate(start);
    setSelectedEndDate(end);
  };

  const handleClearFilters = () => {
    setStatusFilter("");
    setSelectedStartDate(null);
    setSelectedEndDate(null);
  };

  // Edit: Location
  const handleLocationEdit = (record: any) => setSelectedLocationEdit(record);

  // Delete: Location
  const handleDelete = async () => {
    if (!deleteRecord) return;
    try {
      setIsLoading(true);
      const response = await api.post("userService/deleteLocation", {
        signature,
        id: deleteRecord.id,
      });
      notify.success(
        response.data?.message[0]?.msg || "Location deleted successfully."
      );
    } catch (error: any) {
      notify.error(error?.data?.message[0]?.msg || "An error occurred.");
    } finally {
      setDeleteRecord(null);
      await updateUI();
      setIsLoading(false);
    }
  };

  const handleBulkDelete = async (keys: any[]) => {
    if (keys) {
      try {
        setIsLoading(true);
        const response = await api.post("userService/bulkDeleteLocations", {
          signature,
          ids: keys,
        });
        notify.success(response.data.message[0].msg);
      } catch (error: any) {
        notify.error(error?.data?.message?.[0]?.msg || "An error occurred.");
      } finally {
        await updateUI();
        setIsLoading(false);
      }
    }
  };

  // Fetch Data
  const getAllCompanies = async () => {
    try {
      const res = await api.post("userService/getAllCompanyList", {
        signature,
      });
      const companies = res?.data?.data?.data || [];
      setCompanies(companies);
    } catch (error) {
      notify.error("Failed to load companies.");
    }
  };

  const getAllLocationsData = async () => {
    const { company_id, company_type } = formDataCompanyChoose;
    if (!company_id) return;
    try {
      const response = await api.post("userService/getLocation", {
        signature,
        company_id: parseInt(company_id),
        company_type,
      });
      const result = response?.data?.data?.data || [];
      setData(result);
      setFilteredData(result);
    } catch (error) {
      notify.error("Failed to load locations!");
    }
  };

  const updateUI = async () => {
    setIsLoading(true);
    const startTime = Date.now();

    try {
      await getAllLocationsData();
    } catch (error) {
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 300 - elapsed);
      if (remaining > 0) {
        setTimeout(() => setIsLoading(false), remaining);
      } else {
        setIsLoading(false);
      }
    }
  };

  // 📤 Export Handlers
  const keys = [
    "location_name",
    "building",
    "floor",
    "room",
    "created_at",
    "status",
  ];
  const cols = ["Location", "Building", "Floor", "Room", "CreatedAt", "Status"];

  const handleExportPDF = () => {
    exportToPDF({
      data: filteredData,
      headers: cols,
      keys: keys,
      fileName: "locations-data",
    });
  };

  const handleExportExcel = () => {
    exportToExcel({
      data: filteredData,
      headers: cols,
      keys: keys,
      fileName: "locations-data",
    });
  };

  //Use Effects HOOK
  useEffect(() => {
    getAllCompanies();
  }, []);

  useEffect(() => {
    if (companies.length) {
      const storedCompany = localStorage.getItem("selectedAssetCompany");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        handleCompanySelection({
          value: parsed.company_id,
          label: `${parsed.company_name} - ${parsed.company_type}`,
        });
      } else {
        handleCompanySelection({
          value: companies[0].id.toString(),
          label: companies[0].name + " - " + companies[0].type,
        });
      }
    }
  }, [companies]);

  useEffect(() => {
    updateUI();
  }, [formDataCompanyChoose]);

  useEffect(() => {
    let newFilteredData = [...data];
    if (statusFilter) {
      newFilteredData = newFilteredData.filter(
        (item) => item.status === statusFilter
      );
    }
    if (selectedStartDate && selectedEndDate) {
      newFilteredData = newFilteredData.filter((item) => {
        const createdAt = new Date(item.created_at);
        return (
          createdAt >= selectedStartDate.toDate() &&
          createdAt <= selectedEndDate.toDate()
        );
      });
    }
    setFilteredData(newFilteredData);
  }, [statusFilter, selectedStartDate, selectedEndDate, data]);

  const columns = [
    {
      title: "Location Name",
      dataIndex: "location_name",
      render: (text: string) => (
        <div className="d-flex align-items-center">
          <i className="ti ti-map-2 me-2 text-muted"></i>
          <h6 className="fs-14 fw-medium table-text-wrap">{text}</h6>
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a?.location_name || "").localeCompare(b?.location_name || ""),
    },
    {
      title: "Building",
      dataIndex: "building",
      render: (text: string) => (
        <>
          {text && (
            <div className="d-flex align-items-center">
              <i className="ti ti-building me-2 text-muted"></i>
              <h6 className="fs-14 fw-medium table-text-wrap">{text}</h6>
            </div>
          )}
        </>
      ),
      sorter: (a: any, b: any) =>
        (a?.building || "").localeCompare(b?.building || ""),
    },
    {
      title: "Floor",
      dataIndex: "floor",
      render: (text: string) => (
        <h6 className="fs-13 fw-medium table-text-wrap">{text}</h6>
      ),
      sorter: (a: any, b: any) =>
        (a?.floor || "").localeCompare(b?.floor || ""),
    },
    {
      title: "Room",
      dataIndex: "room",
      render: (text: string) => (
        <h6 className="fs-13 fw-medium table-text-wrap">{text}</h6>
      ),
      sorter: (a: any, b: any) => (a?.room || "").localeCompare(b?.room || ""),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: any) => (
        <span
          className={`badge d-inline-flex align-items-center badge-xs ${
            text === "Active"
              ? "badge-success"
              : text === "Inactive"
              ? "badge-danger"
              : "badge-warning"
          }`}
        >
          <i className="ti ti-point-filled me-1"></i>
          {text}
        </span>
      ),
      sorter: (a: any, b: any) => a?.Status?.length - b?.Status?.length,
    },
    {
      title: "",
      dataIndex: "actions",
      render: (text: any, record: any) => (
        <div className="action-icon d-inline-flex">
          <Link
            to="#"
            title="Edit Location"
            className="me-2"
            data-bs-toggle="modal"
            data-inert={true}
            data-bs-target="#edit_location"
            onClick={() => handleLocationEdit(record)}
          >
            <i className="ti ti-edit" />
          </Link>
          <Link
            to="#"
            title="Delete Location"
            data-bs-toggle="modal"
            data-inert={true}
            data-bs-target="#delete_modal"
            onClick={() => setDeleteRecord(record)}
          >
            <i className="ti ti-trash" />
          </Link>
        </div>
      ),
    },
  ];

  return (
    <>
      {isLoading && <Loader />}
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Asset Location</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Administration</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Asset Location
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="me-2 mb-2" title="Choose Company">
                <CommonSelect
                  className="select"
                  options={companyOptions}
                  defaultValue={{
                    value: formDataCompanyChoose.company_id,
                    label:
                      truncateCompanyText(
                        formDataCompanyChoose.company_name,
                        20
                      ) +
                      "-" +
                      formDataCompanyChoose.company_type,
                  }}
                  onChange={handleCompanySelection}
                />
              </div>

              <div className="dropdown me-2 mb-2" title="Export Data Options">
                <Link
                  to="#"
                  className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                  data-bs-toggle="dropdown"
                >
                  <i className="ti ti-file-export me-1" />
                  Export
                </Link>
                <ul className="dropdown-menu p-3">
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      onClick={handleExportPDF}
                    >
                      <i className="ti ti-file-type-pdf me-1" />
                      Export as PDF
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      onClick={handleExportExcel}
                    >
                      <i className="ti ti-file-type-xls me-1" />
                      Export as Excel
                    </Link>
                  </li>
                </ul>
              </div>

              <div className="mb-2 me-2">
                <Link
                  title="Add New Location"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#add_location"
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-plus me-2" />
                  Add New Location
                </Link>
              </div>

              <div className="ms-2 head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Asset Location List</h5>

              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div
                  className="me-3"
                  style={{ minWidth: "120px" }}
                  title="Status Filter"
                >
                  <CommonSelect
                    options={[
                      { value: "", label: "All" },
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                    defaultValue={
                      statusFilter
                        ? { value: statusFilter, label: statusFilter }
                        : { value: "", label: "All" }
                    }
                    onChange={(selected: any) =>
                      setStatusFilter(selected?.value || "")
                    }
                    placeHolder="Select Status"
                  />
                </div>

                <div className="me-3">
                  <div
                    className="d-flex align-items-center"
                    title="Date Range Filter"
                  >
                    <PredefinedDateRanges onDateChange={handleDateChange} />
                  </div>
                </div>

                <div className="me-2" title="Clear Filters">
                  <button
                    style={{ width: "120px" }}
                    className="btn btn-outline-danger d-flex align-items-center position-relative justify-content-center"
                    onClick={handleClearFilters}
                    disabled={activeFiltersCount === 0}
                  >
                    <i className="ti ti-x me-2" />
                    Clear
                    {activeFiltersCount > 0 && (
                      <span className="badge bg-danger rounded-pill ms-2">
                        {activeFiltersCount}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="card-body p-0">
              <Table
                dataSource={filteredData}
                columns={columns}
                Selection={true}
                onBulkDelete={handleBulkDelete}
              />
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              ZYVO
            </Link>
          </p>
        </div>
      </div>

      {/* Add Location Component */}
      <AddLocation
        selectedCompany={formDataCompanyChoose}
        updateUI={updateUI}
      />

      {/* Edit Location Component */}
      <EditLocation
        selectedCompany={formDataCompanyChoose}
        selectedLocation={selectedLocationEdit}
        updateUI={updateUI}
      />

      {/* Delete Modal */}
      <DeleteModal
        modalId="delete_modal"
        onDelete={handleDelete}
        title="Delete Item"
        message="Are you sure you want to delete this item? This action cannot be undone."
      />
    </>
  );
};

export default AssetsLocation;
